import {
    ApiQueryEndpoints,
    ApiQuery,
    QueryResponse,
    SortDirections,
    FilterMetadata,
    FilterOperators,
} from "~/modules/filters/types";
import {
    ApiQueryFetcher,
    ApiQueryStateManager,
    ApiSearchLayout,
} from "~/modules/filters/components/ApiSearchLayout";
import { ExportTableNames } from "~/modules/downloads/types";
import { SearchControlModes } from "~/modules/filters/components/FilterControls/types";
import { Manager } from "../..";
import { Asset, AssetColumns } from "~/modules/assets/types";
import { AssetTable } from "~/modules/assets/components/AssetTable";
import { ColumnMetadata } from "~/components/DataGrid";
import { AssetTableRowData } from "~/modules/assets/components/AssetTable/types";
import { NETWORKS_COLUMN } from "~/modules/assets/components/AssetTable/columns/Networks";
import { HOLDING_ADDRESS_COUNT_COLUMN } from "~/modules/assets/components/AssetTable/columns/Holders";
import { CONTRACT_ADDRESS_COLUMN } from "~/modules/assets/components/AssetTable/columns/ContractAddress";
import { BRIDGED_TOKEN_VALUE_DOLLAR_COLUMN } from "~/modules/assets/components/AssetTable/columns/BridgedTokenValueDollar";
import { INCORPORATION_COUNTRY_COLUMN } from "~/modules/assets/components/AssetTable/columns/IncorporationCountry";
import { LICENSE_OR_REGULATORY_FRAMEWORK } from "~/modules/assets/components/AssetTable/columns/RegulatoryFramework";
import { ASSET_CLASS_COLUMN } from "~/modules/assets/components/AssetTable/columns/AssetClass";
import { ASSET_NAME_AND_TICKER_COLUMN_EXPANDABLE } from "~/modules/assets/components/AssetTable/columns/AssetName";
import { TOKENIZATION_SPONSORSHIP_TYPE_COLUMN } from "~/modules/assets/components/AssetTable/columns/TokenizationSponsorshipType";
import { PLATFORM_COLUMN } from "~/modules/assets/components/AssetTable/columns/Platform";
import { getTokenizationSponsorshipLabel } from "~/components/TokenizationSponsorshipBadge";
import { NET_ASSET_VALUE_COLUMN } from "~/modules/assets/components/AssetTable/columns/NetAssetValue";
import {
    PERFORMANCE_1_MONTH_COLUMN,
    PERFORMANCE_1_YEAR_COLUMN,
} from "~/modules/assets/components/AssetTable/columns/Performance";

// // // //

const MANAGER_ASSETS_TABLE_COLUMNS: ColumnMetadata<
    AssetTableRowData,
    AssetColumns
>[] = [
    ASSET_NAME_AND_TICKER_COLUMN_EXPANDABLE,
    BRIDGED_TOKEN_VALUE_DOLLAR_COLUMN,
    NET_ASSET_VALUE_COLUMN,
    PERFORMANCE_1_MONTH_COLUMN,
    PERFORMANCE_1_YEAR_COLUMN,
    HOLDING_ADDRESS_COUNT_COLUMN,
    ASSET_CLASS_COLUMN,
    {
        ...TOKENIZATION_SPONSORSHIP_TYPE_COLUMN,
        label: (
            <>
                Tokenization
                <br />
                Sponsorship
            </>
        ),
    },
    PLATFORM_COLUMN,
    NETWORKS_COLUMN,
    INCORPORATION_COUNTRY_COLUMN,
    LICENSE_OR_REGULATORY_FRAMEWORK,
    CONTRACT_ADDRESS_COLUMN,
];

// // // //

export function ManagerAssets(props: {
    manager: Manager;
    assetsQuery: ApiQuery;
    filterMetadata: FilterMetadata[];
}) {
    const { assetsQuery, manager } = props;

    const initialState: QueryResponse<Asset> = {
        results: [],
        sort: {
            field: AssetColumns.bridgedTokenValueDollar,
            direction: SortDirections.desc,
        },
        pagination: {
            page: 1,
            perPage: 25,
            resultCount: 25,
            pageCount: 1,
        },
    };

    // Only show relevant filters with multiple options for this manager.
    const filterMetadata = props.filterMetadata
        .filter((fm) =>
            [
                AssetColumns.assetClassID,
                AssetColumns.networkIDs,
                AssetColumns.issuerID,
                AssetColumns.tokenizationSponsorshipType,
            ].includes(fm.field as AssetColumns),
        )
        .filter((fm) => fm.selectOptions && fm.selectOptions.length > 1)
        .map((fm) => {
            if (fm.field !== AssetColumns.tokenizationSponsorshipType)
                return fm;

            return {
                ...fm,
                content: { ...fm.content, label: "Token Sponsor Type" },
                selectOptions: fm.selectOptions?.map((option) => ({
                    ...option,
                    content: {
                        ...option.content,
                        label: getTokenizationSponsorshipLabel(
                            String(option.value),
                            option.content.label,
                        ),
                    },
                })),
            };
        });

    return (
        <div className="mt-6">
            <ApiQueryStateManager
                defaultFilters={assetsQuery}
                modifyApiQuery={(apiQuery) => {
                    if (!apiQuery.filter) {
                        apiQuery.filter = {
                            operator: FilterOperators.And,
                            filters: [
                                {
                                    field: "manager_id",
                                    operator: FilterOperators.Equals,
                                    value: manager.id as number,
                                },
                            ],
                        };
                    }

                    return apiQuery;
                }}
            >
                {({ appliedQuery, internalQuery, resetQuery }) => (
                    <ApiQueryFetcher
                        appliedQuery={appliedQuery.query}
                        internalQuery={internalQuery.query}
                        endpoint={ApiQueryEndpoints.AssetV4}
                        initialState={initialState}
                    >
                        {(state) => {
                            return (
                                <ApiSearchLayout
                                    title="Assets"
                                    filterMetadata={filterMetadata}
                                    onResetQuery={() => {
                                        resetQuery();
                                    }}
                                    appliedQuery={appliedQuery}
                                    internalQuery={internalQuery}
                                    queryFetcherState={state}
                                    controls={{
                                        exportData: {
                                            enabled: true,
                                            exportTableName:
                                                ExportTableNames.assets_v4,
                                        },
                                        search: {
                                            enableBasic: true,
                                            enablePro: false,
                                            mode: SearchControlModes.Pro,
                                        },
                                        apiTools: {
                                            enabled: true,
                                            endpoint: ApiQueryEndpoints.AssetV4,
                                        },
                                        queryExamples: {
                                            items: [],
                                            onSelectItem: (selectedExample) => {
                                                internalQuery.update(
                                                    selectedExample.query,
                                                );
                                            },
                                        },
                                        sharing: {
                                            enabled: false,
                                            url: appliedQuery.url,
                                        },
                                    }}
                                >
                                    <AssetTable
                                        rows={
                                            state.appliedQueryReq.data.results
                                        }
                                        loading={state.appliedQueryReq.loading}
                                        paginationState={
                                            state.appliedQueryReq.data
                                                .pagination
                                        }
                                        columns={MANAGER_ASSETS_TABLE_COLUMNS}
                                        analyticsTableName="manager-individual-assets"
                                        query={appliedQuery.query}
                                        onChangeQuery={appliedQuery.update}
                                        fixedHeader={true}
                                        fixedHeaderClassName="top-[6.8rem]"
                                    />
                                </ApiSearchLayout>
                            );
                        }}
                    </ApiQueryFetcher>
                )}
            </ApiQueryStateManager>
        </div>
    );
}
